import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Container,
  Alert,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { StageConfig } from '../types';

interface StageWrapperProps {
  config: StageConfig;
  currentStage: number;
  totalStages: number;
  stages: string[];
  onNext: () => void;
  onBack: () => void;
  // Save action removed; legacy
  onStageSelect?: (stageIndex: number) => void;
  children: React.ReactNode;
  isProcessing?: boolean;
  processingMessage?: string;
  canProceed?: boolean;
}

const StageWrapper: React.FC<StageWrapperProps> = ({
  config,
  currentStage,
  totalStages,
  stages,
  onNext,
  onBack,
  onStageSelect,
  children,
  isProcessing = false,
  processingMessage = 'Processing...',
  canProceed = true,
}) => {
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Progress Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={currentStage} alternativeLabel>
          {stages.map((label, index) => (
            <Step 
              key={label}
              sx={{ 
                cursor: onStageSelect ? 'pointer' : 'default',
                '& .MuiStepLabel-root': {
                  cursor: onStageSelect ? 'pointer' : 'default',
                  '&:hover': onStageSelect ? {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                  } : {}
                }
              }}
              onClick={() => onStageSelect && onStageSelect(index)}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Stage Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {config.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {config.description}
        </Typography>
      </Paper>

      {/* Processing Indicator */}
      {isProcessing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="body2">{processingMessage}</Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Box>
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ p: 3, mb: 3, minHeight: '400px' }}>
        {children}
      </Paper>

      {/* Navigation Buttons */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            disabled={currentStage === 0 || isProcessing}
          >
            Back
          </Button>

          {/* Save Progress removed (legacy) */}

          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={onNext}
            disabled={!canProceed || isProcessing}
          >
            {currentStage === totalStages - 1 ? 'Complete' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default StageWrapper;
